import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import GenericSelect from '../../../components/ui/GenericSelect';
import Button from '../../../components/ui/Button';
import DateTimePicker from '../../../components/ui/DateTimePicker';
import { Badge, Grid, Table } from '../../performance-operations/styles';
import { formatNumber } from '../../performance-operations/config';
import { formatDate } from '../../../utils/Utils';
import { historySeries } from '../config';

// P7a — E14 sekmeleri (analiz §17): özet, hedefler, yetkinlikler, değerlendirmeler, görüşmeler, gelişim (TD + İK), yetenek (TD + İK),
// geçmiş. Kart içinde kaydırma yok; sayfa gövdesi tek kaydırmadır. Grafiklerde "Tablo Olarak Göster" alternatifi. 1:1 yalnız
// sayı; 360'ta değerlendirici kimliği ve yanıt zamanı hiç gelmez; TD verisi yalnız İK + TD açıkken gelir (sunucu kapısı).
const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
`;


const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};
    font-size: ${({ theme }) => theme.metrics.font.size.sm};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
  & > div { width: 240px; max-width: 100%; }
  @media (max-width: 768px) { & > div { width: 100%; } }
`;

const num = (v, lang, digits = 2) => (v == null ? '-' : formatNumber(v, lang, digits));
const day = (v) => (v ? String(v).slice(0, 10) : '');
// Yerel süzgeçler (analiz §17: dönem, sonuç hali, hedef durumu, geçmiş tarih aralığı) istemcide, yüklenmiş profil verisi üzerinde.
// Aralık: { from, to } "yyyy-MM-dd" (DateTimePicker çıktısı); iki uç da dahil.
export const inRange = (at, range) => {
  if (!range || (!range.from && !range.to)) return true;
  const d = day(at);
  return (!range.from || d >= range.from) && (!range.to || d <= range.to);
};

function RangeFields({ range, onChange }) {
  const { t } = useTranslation();
  return (
    <>
      <div><DateTimePicker label={t('performance.profile.filterFrom')} value={range.from || null} onChange={(v) => onChange({ ...range, from: v })} /></div>
      <div><DateTimePicker label={t('performance.profile.filterTo')} value={range.to || null} onChange={(v) => onChange({ ...range, to: v })} /></div>
    </>
  );
}
const date = (v, lang) => (v ? formatDate(v, lang) : '-');

// ------------------------------------------------------------------ özet

export function SummaryTab({ profile, onOpenTimeline, onOpenAction }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const theme = useTheme();
  const [showTable, setShowTable] = useState(false);
  const [range, setRange] = useState({});
  const series = historySeries(profile.history);
  const timeline = profile.timeline.filter((e) => inRange(e.at, range));
  return (
    <Grid>
      <PanelCard
        panelKey="profile-history"
        span={7}
        title={t('performance.profile.historyChart')}
        empty={series.length === 0}
        emptyText={t('performance.profile.noHistory')}
        tableToggle
        showTable={showTable}
        onToggleTable={() => setShowTable((s) => !s)}
      >
        {showTable ? (
          <Table>
            <thead><tr><th>{t('performance.profile.period')}</th><th>{t('performance.profile.finalNormalized')}</th><th>{t('performance.profile.goalScore')}</th></tr></thead>
            <tbody>{series.map((s) => <tr key={s.name}><td>{s.name}</td><td>{num(s.final, lang)}</td><td>{num(s.goal, lang)}</td></tr>)}</tbody>
          </Table>
        ) : (
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={series} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                <CartesianGrid stroke={theme.colors.surface.border} />
                <XAxis dataKey="name" tick={{ fontSize: theme.metrics.font.size.xs }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: theme.metrics.font.size.xs }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="final" name={t('performance.profile.finalNormalized')} stroke={theme.colors.primary.main} connectNulls />
                <Line type="monotone" dataKey="goal" name={t('performance.profile.goalScore')} stroke={theme.colors.secondary.dark} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </PanelCard>
      <PanelCard panelKey="profile-open-tasks" span={5} title={t('performance.profile.openTasks')} empty={profile.openTasks.length === 0} emptyText={t('performance.profile.noOpenTasks')}>
        <List>
          {profile.openTasks.map((task) => (
            <li key={task.taskId}>
              <span>{t(`performance.operations.taskKey.${task.taskKey}`, { defaultValue: task.taskKey })}</span>
              <span>
                {task.overdueDays != null
                  ? <Badge $tone="danger">{t('performance.profile.overdueDays', { days: task.overdueDays })}</Badge>
                  : <Muted>{date(task.dueAt, lang)}</Muted>}
              </span>
            </li>
          ))}
        </List>
      </PanelCard>
      <PanelCard panelKey="profile-actions" span={6} title={t('performance.profile.actions')} empty={profile.actions.length === 0 && !(profile.development?.plans || []).length} emptyText={t('performance.profile.noActions')}>
        <List>
          {profile.actions.map((a) => (
            <li key={a.actionId}>
              <Button variant="ghost" data-action="open-action" onClick={() => onOpenAction?.(a)}>{a.title}</Button>
              <Muted>{t(`performance.action.statusValue.${a.status}`, { defaultValue: a.status })} · {date(a.dueDate, lang)}</Muted>
            </li>
          ))}
          {(profile.development?.plans || []).map((p) => (
            <li key={p.planId}>
              <span>{p.title}</span>
              <Muted>{t('performance.profile.planProgress', { done: p.doneCount, total: p.actionCount })}</Muted>
            </li>
          ))}
        </List>
      </PanelCard>
      <PanelCard panelKey="profile-result-changes" span={6} title={t('performance.profile.resultChanges')} empty={profile.resultChanges.length === 0} emptyText={t('performance.profile.noResultChanges')}>
        <ResultChangeList changes={profile.resultChanges} />
      </PanelCard>
      <PanelCard panelKey="profile-timeline" span={12} title={t('performance.profile.timeline')}>
        <FilterRow>
          <RangeFields range={range} onChange={setRange} />
        </FilterRow>
        {timeline.length === 0 ? <Muted>{t('performance.profile.noTimeline')}</Muted> : null}
        <List>
          {timeline.slice(0, 20).map((e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${e.kind}-${e.refId || i}-${e.at}`}>
              <Button variant="ghost" data-action="timeline-item" onClick={() => onOpenTimeline(e)}>
                {t(`performance.profile.timelineKind.${e.kind}`)}{e.label ? `: ${e.label}` : ''}
                {e.code && e.kind === 'feedback' ? ` · ${t(`performance.profile.feedbackCategory.${e.code}`, { defaultValue: e.code })}` : ''}
              </Button>
              <Muted>{date(e.at, lang)}</Muted>
            </li>
          ))}
        </List>
      </PanelCard>
    </Grid>
  );
}

function ResultChangeList({ changes }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (
    <List>
      {changes.map((c, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={`${c.kind}-${i}`}>
          <span>
            {t(`performance.profile.changeKind.${c.kind}`)}
            {c.fromLabel || c.toLabel ? `: ${c.fromLabel || '-'} → ${c.toLabel || '-'}` : ''}
            {c.note ? <Muted> · {c.note}</Muted> : null}
          </span>
          <Muted>{date(c.at, lang)}</Muted>
        </li>
      ))}
    </List>
  );
}

// ------------------------------------------------------------------ hedefler

export function GoalsTab({ profile, onOpenGoal }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [status, setStatus] = useState(null);
  const statuses = [...new Set(profile.goals.map((g) => g.status))];
  const goals = profile.goals.filter((g) => !status || g.status === status);
  return (
    <Grid>
      <PanelCard panelKey="profile-goals" span={12} title={t('performance.profile.tabs.goals')} empty={profile.goals.length === 0} emptyText={t('performance.profile.noGoals')}>
        <FilterRow>
          <div>
            <GenericSelect
              label={t('performance.profile.filterGoalStatus')}
              value={status}
              onChange={setStatus}
              options={statuses.map((x) => ({ id: x, name: t(`performance.goal.status.${x}`, { defaultValue: x }) }))}
            />
          </div>
        </FilterRow>
        <Table>
          <thead>
            <tr>
              <th>{t('performance.profile.goal')}</th>
              <th>{t('performance.profile.goalStatus')}</th>
              <th>{t('performance.profile.weight')}</th>
              <th>{t('performance.profile.completion')}</th>
              <th>{t('performance.profile.krProgress')}</th>
              <th>{t('performance.profile.lastUpdate')}</th>
            </tr>
          </thead>
          <tbody>
            {goals.map((g) => (
              <tr key={g.goalId} data-goal={g.goalId}>
                <td><Button variant="ghost" onClick={() => onOpenGoal(g)}>{g.title}</Button></td>
                <td>{t(`performance.goal.status.${g.status}`, { defaultValue: g.status })}</td>
                <td>{g.weightPct != null ? `%${num(g.weightPct, lang)}` : '-'}</td>
                <td>{g.completionPct != null ? `%${num(g.completionPct, lang)}` : '-'}</td>
                <td>{g.avgKeyResultProgress != null ? `%${num(g.avgKeyResultProgress, lang)}` : '-'}</td>
                <td>{date(g.lastCheckInAt, lang)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </PanelCard>
    </Grid>
  );
}

// ------------------------------------------------------------------ yetkinlikler (M54, M55)

export function CompetenciesTab({ profile }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (
    <Grid>
      <PanelCard
        panelKey="profile-competencies"
        span={12}
        title={t('performance.profile.tabs.competencies')}
        empty={profile.competencies.length === 0}
        emptyText={profile.review.visible ? t('performance.profile.noCompetencies') : t('performance.profile.reviewNotVisible')}
      >
        <Table>
          <thead><tr><th>{t('performance.profile.competency')}</th><th>{t('performance.profile.average')}</th><th>{t('performance.profile.expected')}</th><th>{t('performance.profile.gapM55')}</th></tr></thead>
          <tbody>
            {profile.competencies.map((c) => (
              <tr key={c.competencyId}>
                <td>{c.name}</td>
                <td>{num(c.average, lang)}</td>
                <td>{c.targetLevel ?? '-'}</td>
                <td>{c.gap != null ? <Badge $tone={Number(c.gap) < 0 ? 'danger' : 'success'}>{num(c.gap, lang)}</Badge> : '-'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </PanelCard>
    </Grid>
  );
}

// ------------------------------------------------------------------ değerlendirmeler (M18 + 360 özeti)

export function ReviewsTab({ profile }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const r = profile.review;
  return (
    <Grid>
      <PanelCard panelKey="profile-review-status" span={4} title={t('performance.profile.reviewStatus')}>
        <List>
          <li><span>{t('performance.profile.selfReview')}</span><span>{r.selfDone ? t('performance.profile.done') : t('performance.profile.notDone')}</span></li>
          <li><span>{t('performance.profile.managerReview')}</span><span>{r.managerDone ? t('performance.profile.done') : t('performance.profile.notDone')}</span></li>
          <li data-metric="M18">
            <span>{t('performance.profile.gapM18')}</span>
            <span>{r.gap != null ? num(r.gap, lang) : t('performance.aiCenter.noData')}</span>
          </li>
          <li><span>{t('performance.profile.weightedOverall')}</span><span>{num(r.weightedOverall, lang)}</span></li>
        </List>
        {r.gap != null ? <Muted>{t('performance.profile.gapHint', { n: r.matchedQuestions })}</Muted> : null}
      </PanelCard>
      <PanelCard
        panelKey="profile-review-questions"
        span={8}
        title={t('performance.profile.feedback360')}
        empty={!r.visible || r.questions.length === 0}
        emptyText={r.visible ? t('performance.profile.noQuestions') : t('performance.profile.reviewNotVisible')}
      >
        {!r.anonymousGroupShown ? <Muted>{t('performance.profile.anonymousHidden')}</Muted> : null}
        <Table>
          <thead><tr><th>{t('performance.profile.question')}</th><th>{t('performance.profile.average')}</th></tr></thead>
          <tbody>
            {r.questions.map((q) => (
              <tr key={q.questionId}>
                <td>{q.text}{(q.comments || []).length ? <Muted> · {t('performance.profile.commentCount', { count: q.comments.length })}</Muted> : null}</td>
                <td>{q.questionType === 'likert' ? num(q.average, lang) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </PanelCard>
    </Grid>
  );
}

// ------------------------------------------------------------------ görüşmeler (yalnız sayı) + geri bildirim (M39)

export function MeetingsTab({ profile }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const m = profile.meetings;
  const f = profile.feedback;
  return (
    <Grid>
      <PanelCard panelKey="profile-meetings" span={6} title={t('performance.profile.meetings')}>
        <List>
          <li><span>{t('performance.profile.completedMeetings')}</span><span>{m.completed}</span></li>
          <li><span>{t('performance.profile.upcomingMeetings')}</span><span>{m.upcoming}</span></li>
          <li><span>{t('performance.profile.lastMeeting')}</span><span>{date(m.lastAt, lang)}</span></li>
          <li><span>{t('performance.profile.acknowledgedAt')}</span><span>{date(m.acknowledgedAt, lang)}</span></li>
          <li><span>{t('performance.profile.objection')}</span><span>{m.objectionStatus ? t(`performance.objections.status.${m.objectionStatus}`, { defaultValue: m.objectionStatus }) : '-'}</span></li>
        </List>
        <Muted>{t('performance.profile.meetingsCountOnly')}</Muted>
      </PanelCard>
      <PanelCard panelKey="profile-feedback" span={6} title={t('performance.profile.feedbackM39')} empty={!f.visible} emptyText={t('performance.profile.feedbackNotVisible')}>
        <List>
          <li data-metric="M39"><span>{t('performance.profile.feedbackCount')}</span><span>{f.count}</span></li>
          <li><span>{t('performance.profile.feedbackCategory.strength')}</span><span>{f.strengths}</span></li>
          <li><span>{t('performance.profile.feedbackCategory.development_area')}</span><span>{f.developmentAreas}</span></li>
        </List>
      </PanelCard>
    </Grid>
  );
}

// ------------------------------------------------------------------ gelişim (TD + İK; M34) ve yetenek (TD + İK)

export function DevelopmentTab({ profile }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const d = profile.development || { plans: [], pips: [] };
  return (
    <Grid>
      <PanelCard panelKey="profile-plans" span={7} title={t('performance.profile.plans')} empty={d.plans.length === 0} emptyText={t('performance.profile.noPlans')}>
        <Table>
          <thead><tr><th>{t('performance.profile.plan')}</th><th>{t('performance.profile.planStatus')}</th><th>{t('performance.profile.planActions')}</th></tr></thead>
          <tbody>
            {d.plans.map((p) => (
              <tr key={p.planId}>
                <td>{p.title}</td>
                <td>{t(`performance.idp.status.${p.status}`, { defaultValue: p.status })}</td>
                <td>{t('performance.profile.planProgress', { done: p.doneCount, total: p.actionCount })}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Muted data-metric="M34">{t('performance.profile.actionCompletionM34', { value: d.actionCompletion != null ? num(d.actionCompletion, lang) : '-' })}</Muted>
      </PanelCard>
      <PanelCard panelKey="profile-pips" span={5} title={t('performance.profile.pips')} empty={d.pips.length === 0} emptyText={t('performance.profile.noPips')}>
        <List>
          {d.pips.map((p) => (
            <li key={p.pipId}>
              <span>{t(`performance.pip.status.${p.status}`, { defaultValue: p.status })}</span>
              <Muted>{date(p.startedAt, lang)} → {date(p.closedAt || p.dueDate, lang)}</Muted>
            </li>
          ))}
        </List>
      </PanelCard>
    </Grid>
  );
}

export function TalentTab({ profile }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const tl = profile.talent || { succession: [] };
  return (
    <Grid>
      <PanelCard panelKey="profile-ninebox" span={6} title={t('performance.profile.nineBox')}>
        <List>
          <li><span>{t('performance.profile.nineBoxCell')}</span><span>{tl.nineBoxCell || '-'}</span></li>
          <li><span>{t('performance.ninebox.performance')}</span><span>{tl.performanceBand ? t(`performance.ninebox.${tl.performanceBand}`, { defaultValue: tl.performanceBand }) : '-'}</span></li>
          <li><span>{t('performance.ninebox.potential')}</span><span>{tl.potentialBand ? t(`performance.ninebox.${tl.potentialBand}`, { defaultValue: tl.potentialBand }) : '-'}</span></li>
          <li><span>{t('performance.profile.potentialScore')}</span><span>{num(tl.potentialScore, lang)} <Muted>{date(tl.potentialAssessedAt, lang)}</Muted></span></li>
        </List>
      </PanelCard>
      <PanelCard panelKey="profile-succession" span={6} title={t('performance.profile.succession')} empty={(tl.succession || []).length === 0} emptyText={t('performance.profile.noSuccession')}>
        <List>
          {(tl.succession || []).map((s) => (
            <li key={s.criticalPositionId}>
              <span>{s.positionTitle}</span>
              <Muted>{t(`performance.succession.readiness.${s.readiness}`, { defaultValue: s.readiness })}</Muted>
            </li>
          ))}
        </List>
      </PanelCard>
    </Grid>
  );
}

// ------------------------------------------------------------------ geçmiş

export function HistoryTab({ profile }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [state, setState] = useState(null);
  const [range, setRange] = useState({});
  const states = [...new Set(profile.history.map((h) => h.resultState))];
  const rows = profile.history.filter((h) => (!state || h.resultState === state) && inRange(h.endDate, range));
  const changes = profile.resultChanges.filter((c) => inRange(c.at, range));
  return (
    <Grid>
      <PanelCard panelKey="profile-history-table" span={12} compact title={t('performance.profile.filters')}>
        <FilterRow>
          <div>
            <GenericSelect
              label={t('performance.profile.filterResultState')}
              value={state}
              onChange={setState}
              options={states.map((x) => ({ id: x, name: t(`performance.profile.state.${x}`, { defaultValue: x }) }))}
            />
          </div>
          <RangeFields range={range} onChange={setRange} />
        </FilterRow>
      </PanelCard>
      <PanelCard panelKey="profile-history-rows" span={7} title={t('performance.profile.historyTitle')} empty={rows.length === 0} emptyText={t('performance.profile.noHistory')}>
        <Table>
          <thead><tr><th>{t('performance.profile.period')}</th><th>{t('performance.profile.resultState')}</th><th>{t('performance.profile.final')}</th><th>{t('performance.profile.goalScore')}</th></tr></thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.periodId}>
                <td>{h.name}</td>
                <td>{t(`performance.profile.state.${h.resultState}`, { defaultValue: h.resultState })}</td>
                <td>{h.finalLabel || num(h.finalValue, lang)}</td>
                <td>{num(h.goalScore, lang)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </PanelCard>
      <PanelCard panelKey="profile-history-changes" span={5} title={t('performance.profile.resultChanges')} empty={changes.length === 0} emptyText={t('performance.profile.noResultChanges')}>
        <ResultChangeList changes={changes} />
      </PanelCard>
    </Grid>
  );
}
